import React from 'react';
import { Event } from '../../../../types/calendar';
import { getWeekDays, isSameDay } from '../../../../utils/date';

interface MonthViewProps {
  currentDate: Date;
  events: Event[];
  onSlotClick: (date: Date) => void;
  onEventClick: (event: Event, e: React.MouseEvent) => void;
}

export default function MonthView({ currentDate, events, onSlotClick, onEventClick }: MonthViewProps) {
  const weekDays = getWeekDays();
  const today = new Date();
  
  // Get all days in the month
  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    // Get days from previous month to fill the first week
    const daysFromPrevMonth = firstDay.getDay();
    const prevMonth = new Date(year, month, 0);
    
    const days: Date[] = [];
    
    // Add days from previous month
    for (let i = daysFromPrevMonth - 1; i >= 0; i--) {
      days.push(new Date(year, month - 1, prevMonth.getDate() - i));
    }
    
    // Add days from current month
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(new Date(year, month, i));
    }
    
    // Add days from next month
    const remainingDays = 42 - days.length; // 6 weeks * 7 days = 42
    for (let i = 1; i <= remainingDays; i++) {
      days.push(new Date(year, month + 1, i));
    }
    
    return days;
  };

  const days = getDaysInMonth();

  return (
    <div>
      {/* Week days header */}
      <div className="grid grid-cols-7 border-b border-gray-200">
        {weekDays.map((day) => (
          <div key={day} className="py-2 text-center text-sm font-medium text-gray-500">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 h-[800px]">
        {days.map((date, index) => {
          const isCurrentMonth = date.getMonth() === currentDate.getMonth();
          const isToday = isSameDay(date, today);
          const dayEvents = events.filter(event => isSameDay(new Date(event.start), date));
          
          return (
            <div
              key={index}
              onClick={() => onSlotClick(date)}
              className={`min-h-[100px] p-2 border border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors relative ${
                !isCurrentMonth ? 'bg-gray-50' : ''
              } ${isToday ? 'bg-blue-50' : ''}`}
            >
              <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full ${
                isToday 
                  ? 'bg-indigo-600 text-white'
                  : isCurrentMonth 
                    ? 'text-gray-900 hover:bg-gray-200'
                    : 'text-gray-400 hover:bg-gray-200'
              }`}>
                {date.getDate()}
              </span>

              {/* Events */}
              <div className="mt-1 space-y-1">
                {dayEvents.map((event) => (
                  <div
                    key={event.id}
                    onClick={(e) => onEventClick(event, e)}
                    className={`
                      px-2 py-1 rounded-lg text-sm font-medium
                      transition-all duration-200 ease-in-out
                      hover:shadow-md hover:scale-[1.02]
                      cursor-pointer
                      ${event.status === 'draft' ? 'opacity-60' : ''}
                      ${event.status === 'published' ? 'opacity-100' : ''}
                      ${event.priority === 'high' ? 'shadow-sm' : ''}
                    `}
                    style={{
                      backgroundColor: `${event.color}15`, // 15% opacity
                      color: event.color,
                      borderColor: `${event.color}30`, // 30% opacity
                      borderWidth: '1px',
                      borderStyle: 'solid'
                    }}
                  >
                    <div className="flex items-center gap-1.5">
                      <div 
                        className="w-1.5 h-1.5 rounded-full"
                        style={{ backgroundColor: event.color }}
                      />
                      <span className="truncate">{event.title}</span>
                    </div>
                    <div 
                      className="text-xs mt-0.5 pl-3"
                      style={{ color: `${event.color}99` }} // 60% opacity
                    >
                      {new Date(event.start).toLocaleTimeString('pt-BR', {
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}